<template>
  <v-row>
    <!-- Left Column: Basic Info -->
    <v-col cols="6">
      <p class="text-styled-h4">Details</p>

      <v-text-field :value="draft[titleField]" :label="`${titleLabel} *`" :placeholder="titleLabel" color="amber"
        required :error="!!errors.title" :error-messages="errors.title ? `${titleLabel} is required` : ''"
        @input="onTitleInput" />

      <v-text-field v-model="draft.sentence" label="Sentence" placeholder="In a sentence" color="amber" />

      <!-- Group select (items only) -->
      <v-select v-if="showGroupField" v-model="draft.group" :items="groupOptions" label="Group *" item-text="title"
        item-value="id" required class="mb-4" :error="!!errors.group"
        :error-messages="errors.group ? 'Group is required' : ''" @change="$emit('clear-error', 'group')" />

      <v-textarea v-model="draft.description" label="Description" placeholder="Description" color="amber" :rows="3"
        auto-grow hide-details class="mb-4" />

      <div class="mt-10" v-if="doc.contribution">
        <p class="text-styled-h4 white--text">How aligned is the offer to the masterbrand?</p>
        <v-slider v-model="draft.stretch" class="align-center" :label="`${draft.stretch} / ${10}`" color="amber"
          :min="0" :max="10" step="1" ticks="always" tick-size="4" thumb-color="amber" track-color="amber accent-1" />

        <p class="text-styled-h4 white--text">Contribution to the business</p>
        <v-slider v-model="draft.contribution" class="align-center" :label="`${draft.contribution} / ${10}`"
          color="amber" :min="0" :max="10" step="1" ticks="always" tick-size="4" thumb-color="amber"
          track-color="amber accent-1" />

        <p class="text-styled-h4 white--text">Importance for reputation</p>
        <v-slider v-model="draft.reputation" class="align-center" :label="`${draft.reputation} / ${10}`" color="amber"
          :min="0" :max="10" step="1" ticks="always" tick-size="4" thumb-color="amber" track-color="amber accent-1" />
      </div>

      <div class="mt-8" v-if="doc.stats">
        <p class="text-styled-h4">Asset class mix</p>
        <v-row class="mb-4">
          <v-col v-for="score in scoreFields" :key="score.key" cols="6">
            <p class="ma-0 pa-0">{{ score.label }}</p>
            <v-slider v-model="draft.scores[score.key]" :min="0" :max="5" step="1" ticks="always" tick-size="4"
              color="amber" track-color="grey lighten-2" class="ma-0 pa-0" hide-details />
          </v-col>
        </v-row>
      </div>
    </v-col>

    <!-- Right Column: Taxonomy + features/benefits -->
    <v-col cols="6">
      <p class="text-styled-h4">Categories</p>

      <offer-taxonomy-select v-model="draft.categories" :items="categoryOptions" :label="categoryLabel" id-prefix="cat"
        @update:items="updateLocal('categories', $event)" />

      <offer-taxonomy-select v-model="draft.assets" :items="assetOptions" :label="assetLabel" id-prefix="asset"
        @update:items="updateLocal('assets', $event)" />

      <offer-taxonomy-select v-model="draft.tags" :items="tagOptions" :label="tagLabel" id-prefix="tag"
        @update:items="updateLocal('tags', $event)" />

      <offer-taxonomy-select v-model="draft.audiences" :items="audienceOptions" :label="audienceLabel" id-prefix="aud"
        @update:items="updateLocal('audiences', $event)" />

      <div class="mt-8">
        <p class="text-styled-h4">{{ featureLabel }} and {{ benefitLabel }}</p>
        <unit-sub-items v-model="draft.features" name="sub" class="mb-4">
          <v-label>
            <span class="black--text">Define the {{ featureWord }} of the offer</span>
          </v-label>
          <p class="ma-0">Type below and press <strong>Add</strong></p>
        </unit-sub-items>

        <unit-sub-items v-model="draft.benefits" name="sub">
          <v-label>
            <span class="black--text">Define the {{ benefitWord }} of the offer</span>
          </v-label>
          <p class="ma-0">Type below and press <strong>Add</strong></p>
        </unit-sub-items>
      </div>
    </v-col>
  </v-row>
</template>

<script>
import { UnitSubItems } from '@/components/units'
import OfferTaxonomySelect from './OfferTaxonomySelect'
import { scoreFields } from './model'

export default {
  name: 'OfferDetailsBody',

  components: {
    UnitSubItems,
    OfferTaxonomySelect,
  },

  props: {
    // The draft being edited (mutated in place via v-model on fields)
    draft: {
      type: Object,
      required: true,
    },
    // Parent doc with .local taxonomy lists and settings flags
    doc: {
      type: Object,
      default: () => ({}),
    },
    // Show the required Group select (true for items, false for groups)
    showGroupField: {
      type: Boolean,
      default: false,
    },
    // Which draft field holds the visible title ('name' for items, 'title' for groups)
    titleField: {
      type: String,
      default: 'title',
    },
    // Visible label for the title input
    titleLabel: {
      type: String,
      default: 'Title',
    },
    // Validation flags owned by the parent: { title: bool, group: bool }
    errors: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      scoreFields,
    }
  },

  computed: {
    groupOptions() {
      return this.doc?.list || []
    },
    categoryOptions() {
      return this.doc?.local?.categories || []
    },
    assetOptions() {
      return this.doc?.local?.assets || []
    },
    tagOptions() {
      return this.doc?.local?.tags || []
    },
    audienceOptions() {
      return this.doc?.local?.audiences || []
    },
    categoryLabel() {
      return this.taxonomyLabel('categories', 'Categories')
    },
    assetLabel() {
      return this.taxonomyLabel('assets', 'Assets')
    },
    tagLabel() {
      return this.taxonomyLabel('tags', 'Tags')
    },
    audienceLabel() {
      return this.taxonomyLabel('audiences', 'Audiences')
    },
    featureLabel() {
      return this.taxonomyLabel('features', 'Features')
    },
    benefitLabel() {
      return this.taxonomyLabel('benefits', 'Benefits')
    },
    featureWord() {
      return this.rawLabel('features', 'features')
    },
    benefitWord() {
      return this.rawLabel('benefits', 'benefits')
    },
  },

  methods: {
    rawLabel(key, fallback) {
      const custom = this.doc?.local?.labels?.[key]
      return custom && custom.trim() ? custom.trim() : fallback
    },

    taxonomyLabel(key, fallback) {
      const custom = this.doc?.local?.labels?.[key]
      if (!custom || !custom.trim()) return fallback
      const trimmed = custom.trim()
      return trimmed.charAt(0).toUpperCase() + trimmed.slice(1)
    },

    onTitleInput(value) {
      this.$set(this.draft, this.titleField, value)
      this.$emit('clear-error', 'title')
    },

    updateLocal(type, newList) {
      if (!this.doc.local) {
        this.$set(this.doc, 'local', {})
      }
      this.$set(this.doc.local, type, newList)
    },
  },
}
</script>
